import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfigStore, useUIStore } from '@/store';
import { useRawRoutes } from '@/features/routes';
import { useGeolocation } from '../../hooks/useGeolocation';
import {
  CLEVELAND_LOCATIONS,
  TIME_FORMATS,
  LOCALES,
  FONT_SIZES,
} from '../../constants/locations';
import type { ClevelandLocation, TimeFormat, FontSize, Locale } from '@/types';

export function ConfigPanel() {
  const { t, i18n } = useTranslation();
  const { setIsEditing } = useUIStore();
  const { getCurrentLocation } = useGeolocation();
  const rawRoutes = useRawRoutes();

  const {
    title,
    setTitle,
    latLng,
    setLocation,
    timeFormat,
    setTimeFormat,
    locale,
    setLocale,
    hiddenRoutes,
    unhideRoute,
    autoscrollEnabled,
    autoscrollInterval,
    setAutoscroll,
    routesPerRow,
    setRoutesPerRow,
    searchRadiusMeters,
    setSearchRadius,
    fontSize,
    setFontSize,
    exportConfig,
    importConfig,
  } = useConfigStore();

  const [selectedLocation, setSelectedLocation] = useState<ClevelandLocation | null>(null);

  const handleLocationChange = async (location: ClevelandLocation) => {
    setSelectedLocation(location);

    if (location.isCurrent) {
      try {
        const currentLatLng = await getCurrentLocation();
        setLocation(currentLatLng);
      } catch {
        // Fall back to default location on error
      }
    } else if (location.lat !== null && location.lng !== null) {
      setLocation({ latitude: location.lat, longitude: location.lng });
    }
  };

  const handleTimeFormatChange = (format: TimeFormat) => {
    setTimeFormat(format);
  };

  const handleLocaleChange = (newLocale: Locale) => {
    setLocale(newLocale);
    i18n.changeLanguage(newLocale);
  };

  const handleFontSizeChange = (size: FontSize) => {
    setFontSize(size);
  };

  const handleExport = () => {
    const config = exportConfig();
    const configStr = JSON.stringify(config, null, 2);
    navigator.clipboard.writeText(configStr).then(() => {
      alert('Configuration copied to clipboard!');
    });
  };

  const handleImport = () => {
    const configStr = prompt('Paste your configuration JSON here:');
    if (!configStr) return;

    try {
      const config = JSON.parse(configStr);
      importConfig(config);
      alert('Configuration imported successfully!');
    } catch {
      alert('Invalid JSON format. Please check your configuration and try again.');
    }
  };

  const handleClose = () => {
    setIsEditing(false);
  };

  const hiddenRoutesList = rawRoutes?.filter((route) =>
    hiddenRoutes.includes(route.global_route_id)
  );

  // Tailwind class definitions
  const inputClasses = "w-full p-2.5 border border-[#cccccc] rounded focus:border-[#30b566] focus:outline-none text-base box-border";
  const selectClasses = "w-full p-2.5 border border-[#cccccc] rounded focus:border-[#30b566] focus:outline-none text-base box-border";
  const formGroupClasses = "mb-4";
  const labelClasses = "block font-medium mb-2 text-[#333333]";
  const dividerClasses = "border-none border-t border-[#e5e5e5] my-6";
  const buttonClasses = "bg-[#e5e5e5] border border-[#cccccc] rounded py-2.5 px-4 cursor-pointer text-sm hover:bg-[#d5d5d5]";

  return (
    <div className="bg-white rounded-lg w-[600px] max-w-[90vw] max-h-[90vh] flex flex-col shadow-[0_4px_20px_rgba(0,0,0,0.3)]">
      <div className="flex justify-between items-center px-6 py-4 border-b border-[#cccccc] bg-[#f5f5f5] rounded-t-lg">
        <h3 className="m-0 text-[1.4em] text-[#333333]">{t('configuration')}</h3>
        <button
          type="button"
          className="bg-none border-none text-[2em] cursor-pointer text-[#666666] leading-none p-0 hover:text-[#333333]"
          onClick={handleClose}
          aria-label="Close"
        >
          &times;
        </button>
      </div>

      <div className="p-6 overflow-y-auto flex-1">
        {/* Location */}
        <div className={formGroupClasses}>
          <label className={labelClasses}>{t('location')}</label>
          <select
            className={selectClasses}
            value={selectedLocation?.name ?? ''}
            onChange={(e) => {
              const loc = CLEVELAND_LOCATIONS.find((l) => l.name === e.target.value);
              if (loc) handleLocationChange(loc);
            }}
          >
            <option value="">{t('select_location')}</option>
            {CLEVELAND_LOCATIONS.map((loc) => (
              <option key={loc.name} value={loc.name}>
                {loc.name}
              </option>
            ))}
          </select>
          <small className="block text-[0.85em] text-[#666666] mt-1">
            Current: {latLng.latitude.toFixed(4)}, {latLng.longitude.toFixed(4)}
          </small>
        </div>

        {/* Title */}
        <div className={formGroupClasses}>
          <label className={labelClasses}>{t('title')}</label>
          <input
            type="text"
            className={inputClasses}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter display title"
          />
        </div>

        <hr className={dividerClasses} />

        {/* Time Format & Language */}
        <div className="flex gap-4">
          <div className={`${formGroupClasses} flex-1`}>
            <label className={labelClasses}>{t('time_format')}</label>
            <select
              className={selectClasses}
              value={timeFormat}
              onChange={(e) => handleTimeFormatChange(e.target.value as TimeFormat)}
            >
              {TIME_FORMATS.map((tf) => (
                <option key={tf.format} value={tf.format}>
                  {t(tf.name)}
                </option>
              ))}
            </select>
          </div>

          <div className={`${formGroupClasses} flex-1`}>
            <label className={labelClasses}>{t('language')}</label>
            <select
              className={selectClasses}
              value={locale}
              onChange={(e) => handleLocaleChange(e.target.value as Locale)}
            >
              {LOCALES.map((l) => (
                <option key={l.lang} value={l.lang}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <hr className={dividerClasses} />

        {/* Hidden Routes */}
        <div className={formGroupClasses}>
          <label className={labelClasses}>{t('hidden_lines')}</label>
          <ul className="list-none p-0 m-0">
            {hiddenRoutesList && hiddenRoutesList.length > 0 ? (
              hiddenRoutesList.map((route) => (
                <li key={route.global_route_id} className="inline-flex items-center bg-[#e5e5e5] py-1 px-2.5 rounded m-0.5 text-sm">
                  {route.route_short_name}
                  <button
                    type="button"
                    className="bg-none border-none ml-2 cursor-pointer text-[#666666] text-xl leading-none p-0 hover:text-[#cc0000]"
                    onClick={() => unhideRoute(route.global_route_id)}
                  >
                    &times;
                  </button>
                </li>
              ))
            ) : (
              <li className="block text-[#666666] italic">{t('no_hidden_lines')}</li>
            )}
          </ul>
        </div>

        <hr className={dividerClasses} />

        {/* Experimental Settings */}
        <h4 className="text-[1.1em] mb-4 text-[#333333]">{t('experimental_features')}</h4>

        {/* Auto-scroll */}
        <div className="flex gap-4">
          <div className={`${formGroupClasses} flex-1`}>
            <label className={labelClasses}>{t('autoscroll')}</label>
            <label className="flex items-center gap-2 font-normal cursor-pointer">
              <input
                type="checkbox"
                className="w-auto"
                checked={autoscrollEnabled}
                onChange={(e) => setAutoscroll(e.target.checked)}
              />
              {t('autoscroll_enabled')}
            </label>
          </div>

          {autoscrollEnabled && (
            <div className={`${formGroupClasses} flex-1`}>
              <label className={labelClasses}>{t('autoscroll_interval')}</label>
              <div className="flex">
                <input
                  type="number"
                  className={`${inputClasses} rounded-r-none flex-1`}
                  value={autoscrollInterval}
                  min={5}
                  max={60}
                  onChange={(e) => setAutoscroll(true, Number(e.target.value))}
                />
                <span className="bg-[#e5e5e5] border border-[#cccccc] border-l-0 rounded-r py-2.5 px-4 text-sm text-[#666666]">
                  {t('seconds')}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Routes per row & Search radius */}
        <div className="flex gap-4">
          <div className={`${formGroupClasses} flex-1`}>
            <label className={labelClasses}>{t('routes_per_row')}</label>
            <div className="flex">
              <input
                type="number"
                className={`${inputClasses} rounded-r-none flex-1`}
                value={routesPerRow}
                min={1}
                max={5}
                onChange={(e) => setRoutesPerRow(Number(e.target.value))}
              />
              <span className="bg-[#e5e5e5] border border-[#cccccc] border-l-0 rounded-r py-2.5 px-4 text-sm text-[#666666]">
                {t('routes')}
              </span>
            </div>
          </div>

          <div className={`${formGroupClasses} flex-1`}>
            <label className={labelClasses}>{t('search_radius')}</label>
            <div className="flex">
              <input
                type="number"
                className={`${inputClasses} rounded-r-none flex-1`}
                value={searchRadiusMeters}
                min={100}
                max={10000}
                step={50}
                onChange={(e) => setSearchRadius(Number(e.target.value))}
              />
              <span className="bg-[#e5e5e5] border border-[#cccccc] border-l-0 rounded-r py-2.5 px-4 text-sm text-[#666666]">
                {t('meters')}
              </span>
            </div>
          </div>
        </div>

        {/* Font Size */}
        <div className={formGroupClasses}>
          <label className={labelClasses}>{t('font_size')}</label>
          <select
            className={selectClasses}
            value={fontSize}
            onChange={(e) => handleFontSizeChange(e.target.value as FontSize)}
          >
            {FONT_SIZES.map((fs) => (
              <option key={fs.value} value={fs.value}>
                {t(fs.name)}
              </option>
            ))}
          </select>
        </div>

        <hr className={dividerClasses} />

        {/* Export/Import */}
        <div className={formGroupClasses}>
          <label className={labelClasses}>{t('export_import')}</label>
          <div className="flex gap-2">
            <button type="button" className={buttonClasses} onClick={handleExport}>
              {t('export_config')}
            </button>
            <button type="button" className={buttonClasses} onClick={handleImport}>
              {t('import_config')}
            </button>
          </div>
        </div>
      </div>

      <div className="px-6 py-4 border-t border-[#cccccc] bg-[#f5f5f5] rounded-b-lg text-right">
        <button
          type="button"
          className="bg-[#30b566] text-white border-none rounded py-3 px-8 cursor-pointer text-base font-medium hover:bg-[#28a058]"
          onClick={handleClose}
        >
          {t('save')}
        </button>
      </div>
    </div>
  );
}
